import { Box, Select as MUISelect } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { useTranslate } from "hooks/useTranslate";
import { isFunction } from "lodash";
import React, { ForwardedRef, useState } from "react";
import { useAppDispatch } from "redux/hooks";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";
import { checkUserAccess } from "utils/CommonFunctions";
import { setFeedbackDetails } from "../Snackbar/snackbar.slice";
import Typography from "../Typography";
import Validation from "../Validation";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

interface Props {
  options?: any;
  color?: string;
  backgroundColor?: string;
  disabled?: boolean;
  showBold?: boolean;
  status?: string;
  initialValue?: string;
  handleChangeUpdate?: (val: any) => void;
  name?: string;
  register?: (val: any) => void;
  error?: any;
  validations?: any;
  accessType?: string;
  className?: string;
}
interface RefType {
  setValue: Function;
}
function Select(props: Props, ref: ForwardedRef<RefType>) {
  const selectColor = (value: any) => {
    if (value === "New")
      return {
        bgColor: colors.Neutral.Neutral200,
        color: tokens.Text.Feedback.Neutral
      };
    else if (value === "In Progress") return { bgColor: colors.Cyan.Cyan100, color: tokens.Text.Feedback.ProgressDark };
    else return { bgColor: colors.Green.Green100, color: tokens.Text.Feedback.SuccessDark };
  };

  const {
    options,
    disabled,
    showBold = true,
    initialValue,
    handleChangeUpdate,
    register,
    name,
    error,
    validations,
    accessType,
    className = ""
  } = props;
  const restrictUser = accessType && !checkUserAccess(accessType);
  const [selectedOption, setSelectedOption] = useState(initialValue);
  const [stateDropdownColor, setStateDropdownColor] = useState(
    initialValue ? selectColor(initialValue) : { bgColor: "", color: "" }
  );
  const [open, setOpen] = useState(false);

  const handleChange = (event: any) => {
    const {
      target: { value }
    } = event;
    setSelectedOption(value);
    setStateDropdownColor(selectColor(value));
    if (handleChangeUpdate) {
      handleChangeUpdate(value);
    }
  };

  const ArrowDown = (props: any) => (
    <svg
      style={{ cursor: "pointer", marginRight: "8px" }}
      width="26px"
      height="26px"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.2858 10.5715L12.0001 14.8572L7.71436 10.5715"
        stroke={props}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  React.useImperativeHandle(ref, () => {
    return {
      setValue: (newValue: any) => {
        setSelectedOption(newValue);
        setStateDropdownColor(selectColor(newValue));
      }
    };
  });
  const dispatch = useAppDispatch();
  const translate = useTranslate();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center"
      }}
      onClick={() => {
        if (restrictUser)
          dispatch(
            setFeedbackDetails({
              shouldShow: true,
              setState: setFeedbackDetails({ shouldShow: false }),
              message: translate("user.restrictAccess")
            })
          );
        else !disabled && setOpen(!open);
      }}
    >
      <MUISelect
        autoWidth
        open={open}
        sx={{
          borderRadius: "8px",
          padding: "0px 0px 0px 4px",
          height: "34px",
          display: "flex",
          alignItems: "center",
          fontWeight: showBold ? "700" : "",
          fontSize: "14px",
          lineHeight: "150%",
          backgroundColor: stateDropdownColor.bgColor,
          color: stateDropdownColor.color,
          maxWidth: "124px",

          ".MuiOutlinedInput-notchedOutline": { border: 0 },
          " &:hover ": { opacity: "0.5" },
          ".MuiSelect-outlined.MuiInputBase-input": {
            padding: "3px 0 3px 3px"
          }
        }}
        IconComponent={() => ArrowDown(stateDropdownColor.color)}
        disabled={restrictUser ? true : disabled}
        value={selectedOption}
        onChange={handleChange}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return <em>Placeholder</em>;
          }
          return selected;
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              "& .MuiMenuItem-root:hover": {
                backgroundColor: stateDropdownColor.bgColor,
                color: stateDropdownColor.color
              },
              "& .MuiMenuItem-root.Mui-selected": {
                backgroundColor: tokens.Background.General.Primary
              },
              "& .MuiMenuItem-root.Mui-selected:hover": {
                backgroundColor: stateDropdownColor.bgColor,
                color: stateDropdownColor.color
              },
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              width: 200
            }
          }
        }}
        displayEmpty
        inputProps={isFunction(register) ? { ...register(name, validations) } : {}}
      >
        {options?.map((option: any) => (
          <MenuItem
            sx={{
              height: "40px"
            }}
            key={option}
            value={option}
            className={`${className}-${option}`}
          >
            <Typography className={`${className}-${option}`} variant="body2">
              {option}
            </Typography>
          </MenuItem>
        ))}
      </MUISelect>
      {error && <Validation name={name} type={error?.type} />}
    </Box>
  );
}
export default React.forwardRef(Select);
